import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  GrantTraderAccessRequest,
  UpdateTraderAccessExpiryRequest
} from "../../../api/Api";

export const countryTraderAccessKeys = {
  all: ["countryTraderAccess"] as const,
  list: (entityId: string, includeRevoked: boolean) =>
    [...countryTraderAccessKeys.all, "list", entityId, includeRevoked] as const
};

export const useCountryTraderGrants = (
  entityId: string | undefined,
  includeRevoked: boolean
) => {
  return useQuery({
    queryKey: countryTraderAccessKeys.list(entityId ?? "", includeRevoked),
    enabled: !!entityId,
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryTraderGrants.list({
        entityId: entityId!,
        includeRevoked
      });
      return response.data;
    }
  });
};

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

const invalidateGrants = (
  queryClient: ReturnType<typeof useQueryClient>,
  entityId: string
) => {
  queryClient.invalidateQueries({
    queryKey: [...countryTraderAccessKeys.all, "list", entityId]
  });
};

export const useGrantCountryTraderAccess = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: GrantTraderAccessRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryTraderGrants.grant(body);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useRevokeCountryTraderAccess = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { entityId: string; userObjectId: string }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryTraderGrants.revoke(vars);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useUpdateCountryTraderAccessExpiry = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateTraderAccessExpiryRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryTraderGrants.updateExpiry(body);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateGrants(queryClient, variables.entityId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};
